import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { FIRST_INDEX, MILLISECONDS_PER_SECOND, MIN_LEN, OTP_LENGTH } from '@/constants';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';

const schema = z.object({
  code: z
    .string()
    .length(OTP_LENGTH, `Code must be exactly ${String(OTP_LENGTH)} digits`)
    .regex(/^\d+$/, 'Code must contain only digits'),
});

interface CodeConfirmationConfig {
  onConfirm: (code: string) => Promise<boolean>;
  onResend: () => Promise<number | undefined>;
  maskedDestination: string;
  formData?: { code?: string };
  loading?: boolean;
  error?: string;
}

interface UseCodeConfirmationState {
  code: string;
  hasSubmitted: boolean;
  isResending: boolean;
  resendCountdown: number;
  codeError: string | undefined;
  isSubmitting: boolean;
  isSubmitDisabled: boolean;
  resendDisabled: boolean;
  maskedDestination: string;
  codeLength: number;
  globalError: string | undefined;
}

interface UseCodeConfirmationHandlers {
  handleCodeChange: (next: string) => void;
  handleSubmit: () => void;
  handleResend: () => void;
}

export interface UseCodeConfirmationReturn {
  state: UseCodeConfirmationState;
  handlers: UseCodeConfirmationHandlers;
}

export function useCodeConfirmation(config: CodeConfirmationConfig): UseCodeConfirmationReturn {
  const { onConfirm, onResend, maskedDestination, formData, loading, error } = config;
  const { handlers: loaderHandlers } = useGlobalLoader();

  const [code, setCode] = useState(
    (formData?.code ?? '').replace(/\D/g, '').slice(MIN_LEN, OTP_LENGTH),
  );
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(MIN_LEN);
  const [isLocallySubmitting, setIsLocallySubmitting] = useState(false);

  const parsed = useMemo(() => schema.safeParse({ code }), [code]);

  const codeError = useMemo(() => {
    if (!hasSubmitted) return undefined;
    if (parsed.success) return undefined;
    return parsed.error.flatten().fieldErrors.code?.[FIRST_INDEX];
  }, [hasSubmitted, parsed]);

  const isSubmitting = (loading ?? false) || isLocallySubmitting;
  const isSubmitDisabled = hasSubmitted && !parsed.success;
  const resendDisabled = isResending || resendCountdown > MIN_LEN;

  const globalError =
    typeof error === 'string' && error.trim().length > FIRST_INDEX ? error : undefined;

  useEffect(() => {
    if (resendCountdown <= MIN_LEN) return;

    const timer = setTimeout(() => {
      setResendCountdown((prev) => prev - 1);
    }, MILLISECONDS_PER_SECOND);

    return () => {
      clearTimeout(timer);
    };
  }, [resendCountdown]);

  const handleResend = useCallback(() => {
    if (isResending || resendCountdown > MIN_LEN) return;

    setIsResending(true);
    void onResend().then((expiresInSeconds) => {
      setIsResending(false);
      if (expiresInSeconds !== undefined) {
        setResendCountdown(expiresInSeconds);
      }
    });
  }, [isResending, resendCountdown, onResend]);

  const handleSubmit = useCallback(() => {
    setHasSubmitted(true);
    const currentParsed = schema.safeParse({ code });
    if (!currentParsed.success) return;

    setIsLocallySubmitting(true);
    loaderHandlers.showLoader();
    void onConfirm(code).finally(() => {
      loaderHandlers.hideLoader();
      setIsLocallySubmitting(false);
    });
  }, [code, loaderHandlers, onConfirm]);

  const handleCodeChange = useCallback((next: string) => {
    setCode(next.replace(/\D/g, '').slice(MIN_LEN, OTP_LENGTH));
  }, []);

  return {
    state: {
      code,
      hasSubmitted,
      isResending,
      resendCountdown,
      codeError,
      isSubmitting,
      isSubmitDisabled,
      resendDisabled,
      maskedDestination,
      codeLength: OTP_LENGTH,
      globalError,
    },
    handlers: {
      handleCodeChange,
      handleSubmit,
      handleResend,
    },
  };
}
